"use client";

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { Scroll } from "@/lib/supabase";
import type { Json } from "@/lib/database.types";
import {
  fetchScrollsPage,
  type ScrollListParams,
  type ScrollPage,
} from "@/lib/api/queries/scrolls";
import {
  collectAllScrollQueryData,
  findScrollInSnapshots,
  patchScrollInfiniteData,
  restoreAllScrollQueryData,
  type ScrollListInfiniteData,
} from "@/lib/client/scroll-query-cache";
import { STALE_TIME_DEFAULT_MS } from "@/lib/react-query/defaults";
import { queryKeys } from "@/lib/react-query/query-keys";

export type { ScrollListParams as ListParams, ScrollPage };

type ListParams = ScrollListParams;
type ApiSuccess<T> = { success: true; data: T };

type CreateScrollPayload = {
  title: string;
  icon?: string | null;
  folder?: string | null;
  folder_id?: string | null;
  parent_id?: string | null;
  is_pinned?: boolean;
  is_starred?: boolean;
  workspace_id?: string | null;
};

type UpdateScrollPayload = {
  id: string;
  title?: string;
  content_json?: Json | null;
  cover_image?: string | null;
  is_archived?: boolean;
  content_html?: string;
  content_css?: string;
  content_js?: string;
  icon?: string | null;
  folder?: string | null;
  folder_id?: string | null;
  parent_id?: string | null;
  is_pinned?: boolean;
  is_starred?: boolean;
};

/** Star toggle only — avoid rolling back unrelated optimistic scroll updates. */
function isStarTogglePayload(payload: UpdateScrollPayload): boolean {
  const keys = Object.keys(payload).filter((k) => k !== "id");
  return keys.length === 1 && keys[0] === "is_starred";
}

function revertSingleScrollInCache(
  qc: ReturnType<typeof useQueryClient>,
  scrollId: string,
  snapshots: Array<[readonly unknown[], ScrollListInfiniteData | undefined]>,
) {
  const previous = findScrollInSnapshots(snapshots, scrollId);
  if (!previous) return;

  qc.setQueriesData<ScrollListInfiniteData>(
    { queryKey: ["scrolls"] },
    (existing) =>
      patchScrollInfiniteData(existing, (items) =>
        items.map((item) => (item.id === scrollId ? previous : item)),
      ),
  );
}

export function useScrollsInfiniteQuery(
  params?: ListParams,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true;
  return useInfiniteQuery({
    queryKey: queryKeys.scrolls(params),
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => fetchScrollsPage(pageParam, params),
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.nextCursor ?? undefined) : undefined,
    staleTime: STALE_TIME_DEFAULT_MS,
  });
}

export function useUpdateScrollMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateScrollPayload) => {
      const res = await fetch("/api/scrolls", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const json = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        const err = new Error(json.error ?? "Failed to update scroll") as Error & {
          code?: string;
        };
        err.code = json.code;
        throw err;
      }
      const json = await res.json();
      return json.data as Scroll;
    },
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: ["scrolls"] });
      const previous = collectAllScrollQueryData(qc);

      qc.setQueriesData<ScrollListInfiniteData>(
        { queryKey: ["scrolls"] },
        (existing) =>
          patchScrollInfiniteData(existing, (items) =>
            items.map((item) =>
              item.id === payload.id
                ? {
                    ...item,
                    ...payload,
                    updated_at: new Date().toISOString(),
                  }
                : item,
            ),
          ),
      );

      return { previous };
    },
    onError: (_err, payload, ctx) => {
      if (!ctx?.previous) return;
      if (isStarTogglePayload(payload)) {
        revertSingleScrollInCache(qc, payload.id, ctx.previous);
        return;
      }
      restoreAllScrollQueryData(qc, ctx.previous);
    },
    onSuccess: (updated) => {
      qc.setQueriesData<ScrollListInfiniteData>(
        { queryKey: ["scrolls"] },
        (existing) =>
          patchScrollInfiniteData(existing, (items) =>
            items.map((item) => (item.id === updated.id ? updated : item)),
          ),
      );
      void qc.invalidateQueries({ queryKey: ["home-momentum"] });
      void qc.invalidateQueries({ queryKey: ["workspace-search"] });
    },
  });
}

export function useCreateScrollMutation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateScrollPayload) => {
      const res = await fetch("/api/scrolls", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const json = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        const err = new Error(json.error ?? "Failed to create scroll") as Error & {
          code?: string;
        };
        err.code = json.code;
        throw err;
      }
      const json: ApiSuccess<Scroll> = await res.json();
      return json.data;
    },
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: ["scrolls"] });
      const previous = collectAllScrollQueryData(qc);

      const tempId = `temp-${crypto.randomUUID()}`;
      const now = new Date().toISOString();
      const optimistic: Scroll = {
        id: tempId,
        title: payload.title,
        user_id: "",
        workspace_id: payload.workspace_id ?? null,
        icon: payload.icon ?? null,
        folder: payload.folder ?? null,
        folder_id: payload.folder_id ?? null,
        trashed_by_folder_id: null,
        parent_id: payload.parent_id ?? null,
        is_pinned: payload.is_pinned ?? false,
        is_starred: payload.is_starred ?? false,
        starred_at: null,
        archived_at: null,
        is_deleted: false,
        deleted_at: null,
        is_published: false,
        publish_paused: false,
        plan_locked: false,
        public_slug: null,
        content_json: null,
        cover_image: null,
        is_archived: false,
        content_html: "",
        content_css: "",
        content_js: "",
        position: 0,
        sort_order: null,
        created_at: now,
        updated_at: now,
      };

      qc.setQueriesData<ScrollListInfiniteData>(
        { queryKey: ["scrolls"] },
        (existing) => {
          if (!existing) {
            return {
              pageParams: [undefined],
              pages: [
                {
                  items: [optimistic],
                  nextCursor: null,
                  hasMore: false,
                  limit: 30,
                },
              ],
            };
          }
          if (existing.pages.length === 0) {
            return {
              ...existing,
              pages: [
                {
                  items: [optimistic],
                  nextCursor: null,
                  hasMore: false,
                  limit: 30,
                },
              ],
            };
          }
          const first = existing.pages[0];
          return {
            ...existing,
            pages: [
              {
                ...first,
                items: [optimistic, ...(first.items ?? [])],
              },
              ...existing.pages.slice(1),
            ],
          };
        },
      );

      return { previous, tempId };
    },
    onError: (_err, _payload, ctx) => {
      if (ctx?.previous) {
        restoreAllScrollQueryData(qc, ctx.previous);
      }
    },
    onSuccess: (created, _payload, ctx) => {
      qc.setQueriesData<ScrollListInfiniteData>(
        { queryKey: ["scrolls"] },
        (existing) =>
          patchScrollInfiniteData(existing, (items) => {
            const idx = items.findIndex((item) => item.id === ctx?.tempId);
            if (idx >= 0) {
              return items.map((item) =>
                item.id === ctx?.tempId ? created : item,
              );
            }
            return [created, ...items];
          }),
      );
      void qc.invalidateQueries({ queryKey: ["home-momentum"] });
      void qc.invalidateQueries({ queryKey: ["workspace-search"] });
    },
  });
}

export function useDeleteScrollMutation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch("/api/scrolls", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });

      if (!res.ok) throw new Error("Failed to delete scroll");
      return id;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["scrolls"] });
      const previous = collectAllScrollQueryData(qc);

      qc.setQueriesData<ScrollListInfiniteData>(
        { queryKey: ["scrolls"] },
        (existing) =>
          patchScrollInfiniteData(existing, (items) =>
            items.filter((item) => item.id !== id),
          ),
      );

      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        restoreAllScrollQueryData(qc, ctx.previous);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scrolls"] });
      void qc.invalidateQueries({ queryKey: ["home-momentum"] });
      void qc.invalidateQueries({ queryKey: ["workspace-search"] });
    },
  });
}
